/**
 * Desired Outcome Store
 *
 * Manages Desired Outcomes — structured 7-question artifacts that answer:
 * "How will you know when you have what you want?"
 *
 * Per Build Contract: Supabase is source of truth. Zustand is a cache.
 */

import { create } from 'zustand';
import {
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
  generateUUID,
} from '../../lib/supabaseSync';

// ── Types ─────────────────────────────────────────────────────

export type ObstacleCategory =
  | 'People'
  | 'Process'
  | 'Technology'
  | 'Policy'
  | 'Budget'
  | 'Other';

export interface DesiredOutcomeObstacle {
  id: string;
  category: ObstacleCategory;
  description: string;
}

export interface DesiredOutcomeStep {
  id: string;
  text: string;
  isStepZero: boolean;
  isThreshold: boolean;
  dependency?: string;
}

export interface DesiredOutcome {
  id: string;
  workspaceId: string;

  title: string;

  // Q1: Who wants it? (exactly one)
  who: string;
  // Q2: What's wanted? (single positive sentence)
  whatWanted: string;
  // Q3: Deeper benefit / meta-outcome
  deeperBenefit: string | null;
  // Q4: Evidence — three levels
  evidenceSufficient: string | null;
  evidenceTargeted: string | null;
  evidenceIdeal: string | null;
  // Q4 timing
  soonestNeeded: string | null;
  lastResponsibleMoment: string | null;
  // Q5: Obstacles
  obstacles: DesiredOutcomeObstacle[];
  // Q6: Ecology
  ecology: string | null;
  ecologyMitigations: string | null;
  // Q7: Steps
  steps: DesiredOutcomeStep[];

  cycleId: string | null;
  /** Optional link to a strategic objective. Neither requires the other. */
  objectiveId: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export type CreateDesiredOutcomeInput = Pick<DesiredOutcome, 'title'> &
  Partial<Omit<DesiredOutcome, 'id' | 'workspaceId' | 'createdAt' | 'updatedAt' | 'createdBy'>>;

export type UpdateDesiredOutcomeInput = Partial<
  Omit<DesiredOutcome, 'id' | 'workspaceId' | 'createdAt' | 'updatedAt' | 'createdBy'>
>;

// ── Store interface ───────────────────────────────────────────

interface DesiredOutcomeState {
  outcomes: DesiredOutcome[];
  isLoading: boolean;
  error: string | null;

  fetchOutcomes: () => Promise<void>;
  createOutcome: (input: CreateDesiredOutcomeInput) => Promise<{ outcome: DesiredOutcome | null; error?: string }>;
  updateOutcome: (id: string, updates: UpdateDesiredOutcomeInput) => Promise<{ success: boolean; error?: string }>;
  deleteOutcome: (id: string) => Promise<{ success: boolean; error?: string }>;
  getOutcomeById: (id: string) => DesiredOutcome | undefined;
}

// ── Row mapper ────────────────────────────────────────────────

function rowToOutcome(r: Record<string, unknown>): DesiredOutcome {
  const rawObstacles = (r.obstacles as unknown[]) ?? [];
  const obstacles: DesiredOutcomeObstacle[] = (Array.isArray(rawObstacles) ? rawObstacles : []).map((o: unknown) => {
    const ob = o as Record<string, unknown>;
    return {
      id: (ob.id as string) ?? generateUUID(),
      category: (ob.category as ObstacleCategory) ?? 'Other',
      description: (ob.description as string) ?? '',
    };
  });

  const rawSteps = (r.steps as unknown[]) ?? [];
  const steps: DesiredOutcomeStep[] = (Array.isArray(rawSteps) ? rawSteps : []).map((s: unknown) => {
    const st = s as Record<string, unknown>;
    return {
      id: (st.id as string) ?? generateUUID(),
      text: (st.text as string) ?? '',
      isStepZero: (st.is_step_zero as boolean) ?? false,
      isThreshold: (st.is_threshold as boolean) ?? false,
      dependency: (st.dependency as string) ?? undefined,
    };
  });

  return {
    id: r.id as string,
    workspaceId: r.workspace_id as string,
    title: (r.title as string) ?? 'Untitled',
    who: (r.who as string) ?? '',
    whatWanted: (r.what_wanted as string) ?? '',
    deeperBenefit: (r.deeper_benefit as string) ?? null,
    evidenceSufficient: (r.evidence_sufficient as string) ?? null,
    evidenceTargeted: (r.evidence_targeted as string) ?? null,
    evidenceIdeal: (r.evidence_ideal as string) ?? null,
    soonestNeeded: (r.soonest_needed as string) ?? null,
    lastResponsibleMoment: (r.last_responsible_moment as string) ?? null,
    obstacles,
    ecology: (r.ecology as string) ?? null,
    ecologyMitigations: (r.ecology_mitigations as string) ?? null,
    steps,
    cycleId: (r.cycle_id as string) ?? null,
    objectiveId: (r.objective_id as string) ?? null,
    createdBy: (r.created_by as string) ?? null,
    createdAt: r.created_at as string,
    updatedAt: r.updated_at as string,
  };
}

function outcomeToRow(b: CreateDesiredOutcomeInput | UpdateDesiredOutcomeInput): Record<string, unknown> {
  const row: Record<string, unknown> = {};

  if ('title' in b && b.title !== undefined) row.title = b.title;
  if ('who' in b) row.who = b.who ?? '';
  if ('whatWanted' in b) row.what_wanted = b.whatWanted ?? '';
  if ('deeperBenefit' in b) row.deeper_benefit = b.deeperBenefit ?? null;
  if ('evidenceSufficient' in b) row.evidence_sufficient = b.evidenceSufficient ?? null;
  if ('evidenceTargeted' in b) row.evidence_targeted = b.evidenceTargeted ?? null;
  if ('evidenceIdeal' in b) row.evidence_ideal = b.evidenceIdeal ?? null;
  if ('soonestNeeded' in b) row.soonest_needed = b.soonestNeeded ?? null;
  if ('lastResponsibleMoment' in b) row.last_responsible_moment = b.lastResponsibleMoment ?? null;
  if ('ecology' in b) row.ecology = b.ecology ?? null;
  if ('ecologyMitigations' in b) row.ecology_mitigations = b.ecologyMitigations ?? null;
  if ('cycleId' in b) row.cycle_id = b.cycleId ?? null;
  if ('objectiveId' in b) row.objective_id = b.objectiveId ?? null;

  if ('obstacles' in b && b.obstacles !== undefined) {
    row.obstacles = b.obstacles.map((o) => ({
      id: o.id,
      category: o.category,
      description: o.description,
    }));
  }

  if ('steps' in b && b.steps !== undefined) {
    row.steps = b.steps.map((s) => ({
      id: s.id,
      text: s.text,
      is_step_zero: s.isStepZero,
      is_threshold: s.isThreshold,
      dependency: s.dependency ?? null,
    }));
  }

  return row;
}

// ── Store ─────────────────────────────────────────────────────

export const useDesiredOutcomeStore = create<DesiredOutcomeState>((set, get) => ({
  outcomes: [],
  isLoading: false,
  error: null,

  fetchOutcomes: async () => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return;

    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('desired_outcome_briefs')
      .select('*')
      .eq('workspace_id', workspaceId)
      .order('created_at', { ascending: false });

    if (error) {
      set({ isLoading: false, error: error.message });
      return;
    }

    set({
      outcomes: (data ?? []).map((r) => rowToOutcome(r as Record<string, unknown>)),
      isLoading: false,
    });
  },

  createOutcome: async (input) => {
    const supabase = getSafeSupabaseClient();
    const workspaceId = getCurrentWorkspaceId();
    if (!supabase || !workspaceId) return { outcome: null, error: 'Not authenticated' };

    const id = generateUUID();
    const row = {
      id,
      workspace_id: workspaceId,
      ...outcomeToRow(input),
    };

    const { data, error } = await supabase
      .from('desired_outcome_briefs')
      .insert(row)
      .select()
      .single();

    if (error) return { outcome: null, error: error.message };

    const outcome = rowToOutcome(data as Record<string, unknown>);
    set((s) => ({ outcomes: [outcome, ...s.outcomes] }));
    return { outcome };
  },

  updateOutcome: async (id, updates) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Not authenticated' };

    set((s) => ({
      outcomes: s.outcomes.map((o) => (o.id === id ? { ...o, ...updates } : o)),
    }));

    const { error } = await supabase
      .from('desired_outcome_briefs')
      .update(outcomeToRow(updates))
      .eq('id', id);

    if (error) {
      await get().fetchOutcomes();
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  deleteOutcome: async (id) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return { success: false, error: 'Not authenticated' };

    set((s) => ({ outcomes: s.outcomes.filter((o) => o.id !== id) }));

    const { error } = await supabase
      .from('desired_outcome_briefs')
      .delete()
      .eq('id', id);

    if (error) {
      await get().fetchOutcomes();
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  getOutcomeById: (id) => get().outcomes.find((o) => o.id === id),
}));
